<template>
  <div class="member-code-page qm-page-bg">
    <van-nav-bar
      title="会员码"
      left-arrow
      class="qm-nav-bar"
      fixed
      placeholder
      @click-left="$router.back()"
    />

    <div class="page-body">
      <div class="code-card qm-glass-card">
        <div class="logo-wrap">
          <img class="avatar-img" :src="avatarImg" alt="avatar" />
          <div class="avatar-ring"></div>
        </div>

        <div class="user-name">{{ userName }}</div>
        <div class="member-badge">VIP 会员</div>

        <van-loading v-if="loading && !memberCode" vertical color="#f5a623">加载中...</van-loading>

        <template v-else-if="memberCode">
          <div class="barcode-wrap">
            <canvas ref="barcodeCanvas" class="barcode-canvas"></canvas>
          </div>

          <div class="code-text">
            <span>{{ displayCode }}</span>
            <span class="view-number" @click="toggleShowFullCode">
              {{ showFullCode ? '隐藏' : '查看' }}
            </span>
          </div>

          <div class="qrcode-wrap">
            <div class="qrcode-border">
              <canvas ref="qrcodeCanvas" class="qrcode-canvas"></canvas>
            </div>
          </div>

          <div class="refresh-tip">
            <van-icon name="replay" class="refresh-icon" :class="{ spinning: refreshing }" />
            <span>{{ countdown }}秒后自动更新</span>
          </div>
        </template>

        <div class="stats-divider"></div>

        <div class="stats-row">
          <div v-for="item in statsList" :key="item.label" class="stat-item">
            <div class="stat-value">{{ item.value }}</div>
            <div class="stat-label">{{ item.label }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import request from '@/utils/request';
import avatarImg from '@/assets/avar.jpg';

const REFRESH_SECONDS = 23;

export default {
  name: 'MemberCode',
  data() {
    return {
      avatarImg,
      userName: '微信用户',
      memberCode: '',
      showFullCode: false,
      countdown: REFRESH_SECONDS,
      countdownTimer: null,
      loading: false,
      refreshing: false,
      statsList: [
        { label: '优惠券', value: 0 },
        { label: '积分', value: 0 },
        { label: '余额', value: 0 },
        { label: '礼品卡', value: 0 },
        { label: '储值卡', value: 0 },
      ],
    };
  },
  computed: {
    ...mapState(['appConfig', 'qmUserToken']),
    displayCode() {
      if (!this.memberCode) return '';
      if (this.showFullCode) {
        return this.memberCode;
      }
      const prefix = this.memberCode.slice(0, 4);
      return `${prefix} **** **** **** ****`;
    },
  },
  created() {
    if (!this.appConfig || !this.qmUserToken) {
      this.$toast.fail('请先验证卡密');
      this.$router.replace('/cardkey');
      return;
    }
    this.fetchMemberCode();
    this.fetchCouponCount();
    this.startCountdown();
  },
  beforeDestroy() {
    this.clearCountdown();
  },
  methods: {
    toggleShowFullCode() {
      this.showFullCode = !this.showFullCode;
    },
    isSuccessCode(code) {
      return code === 0 || code === '0';
    },
    async fetchMemberCode() {
      if (!this.appConfig || !this.qmUserToken) return;

      const isFirstLoad = !this.memberCode;
      if (isFirstLoad) {
        this.loading = true;
      } else {
        this.refreshing = true;
      }

      try {
        const res = await request.post('/qimai/query-member-code', {
          appid: this.appConfig.appid,
          storeId: this.appConfig.storeId,
        });

        if (!this.isSuccessCode(res.code) || !res.data) {
          throw new Error(res.message || '获取会员码失败');
        }

        this.memberCode = String(res.data);
        this.countdown = REFRESH_SECONDS;
        this.renderCodes();
      } catch (error) {
        console.error('获取会员码失败:', error);
        this.$toast.fail(error.message || '获取会员码失败');
      } finally {
        this.loading = false;
        this.refreshing = false;
      }
    },
    async fetchCouponCount() {
      try {
        const res = await request.post('/qimai/coupon/list', {
          qmUserToken: this.qmUserToken,
        });
        const couponList = (res.data && res.data.data) || [];
        this.updateStat('优惠券', couponList.length);
      } catch (error) {
        console.error('获取优惠券数量失败:', error);
      }
    },
    updateStat(label, value) {
      const item = this.statsList.find((stat) => stat.label === label);
      if (item) {
        item.value = value;
      }
    },
    renderCodes() {
      this.$nextTick(() => {
        this.renderBarcode();
        this.renderQrcode();
      });
    },
    renderBarcode() {
      const canvas = this.$refs.barcodeCanvas;
      if (!canvas || !this.memberCode) return;
      JsBarcode(canvas, this.memberCode, {
        format: 'CODE128',
        width: 1,
        height: 80,
        displayValue: false,
        margin: 0,
      });
    },
    renderQrcode() {
      const canvas = this.$refs.qrcodeCanvas;
      if (!canvas || !this.memberCode) return;
      QRCode.toCanvas(canvas, this.memberCode, {
        width: 200,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#ffffff',
        },
      });
    },
    startCountdown() {
      this.clearCountdown();
      this.countdownTimer = setInterval(() => {
        if (this.countdown <= 1) {
          this.fetchMemberCode();
        } else {
          this.countdown -= 1;
        }
      }, 1000);
    },
    clearCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.member-code-page {
  min-height: 100vh;
}

.page-body {
  padding: 24px 20px 40px;
}

.code-card {
  position: relative;
  padding: 60px 24px 28px;
  margin-top: 36px;
  box-shadow: var(--qm-shadow-glow);
}

.logo-wrap {
  position: absolute;
  top: -36px;
  left: 50%;
  transform: translateX(-50%);

  .avatar-ring {
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    border: 2px solid var(--qm-accent);
    opacity: 0.5;
  }
}

.avatar-img {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  object-fit: cover;
  background: var(--qm-bg-surface);
  position: relative;
  z-index: 1;
}

.user-name {
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  color: var(--qm-text-primary);
  margin-bottom: 6px;
}

.member-badge {
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: var(--qm-accent);
  letter-spacing: 2px;
  margin-bottom: 24px;
}

.barcode-wrap {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-bottom: 12px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid var(--qm-border);
  border-radius: var(--qm-radius-sm);
}

.barcode-canvas {
  width: 100%;
  height: 80px;
}

.code-text {
  text-align: center;
  font-size: 14px;
  color: var(--qm-text-secondary);
  margin-bottom: 20px;
  word-break: break-all;

  .view-number {
    margin-left: 8px;
    color: var(--qm-accent);
    cursor: pointer;
    font-size: 13px;
  }
}

.qrcode-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;

  .qrcode-border {
    padding: 12px;
    background: #fff;
    border-radius: var(--qm-radius-md);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  }
}

.qrcode-canvas {
  width: 180px !important;
  height: 180px !important;
}

.refresh-tip {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: var(--qm-text-muted);
  margin-bottom: 24px;

  .refresh-icon {
    margin-right: 4px;
    font-size: 14px;
    color: var(--qm-accent);

    &.spinning {
      animation: spin 1s linear infinite;
    }
  }
}

.stats-divider {
  border-top: 1px dashed var(--qm-border);
  margin-bottom: 20px;
}

.stats-row {
  display: flex;
  justify-content: space-between;
}

.stat-item {
  flex: 1;
  text-align: center;

  .stat-value {
    font-size: 18px;
    font-weight: 700;
    color: var(--qm-accent);
    margin-bottom: 6px;
  }

  .stat-label {
    font-size: 11px;
    color: var(--qm-text-muted);
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
